import { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import {
  ContentLayout, Header, Tabs, Container, SpaceBetween, Box,
  Button, Cards, Alert,
} from "@cloudscape-design/components";
import { adminService } from "../../services/adminService";
import UserManagement from "./UserManagement";
import GroupManagement from "./GroupManagement";
import MenuManagement from "./MenuManagement";
import SecurityMonitoring from "./SecurityMonitoring";
import AdminUsers from "./AdminUsers";
import UsageAnalytics from "./UsageAnalytics";
import TopNavigation from "@cloudscape-design/components/top-navigation";
import { Flashbar } from "@cloudscape-design/components";
import PoweredByFooter from "../common/PoweredByFooter";
import { useNotification } from "../../context/NotificationContext";

export default function AdminInterface() {
  const { adminUser, adminLogout } = useAuth();
  const { notifications, removeNotification } = useNotification();
  const history = useHistory();
  const [stats, setStats] = useState({ totalUsers: "—", activeGroups: "—", menuItems: "—", loading: true });
  const [activeTab, setActiveTab] = useState("dashboard");
  const [accessAlert, setAccessAlert] = useState(null);

  const loadStats = async () => {
    setStats(s => ({ ...s, loading: true }));
    try {
      const data = await adminService.getDashboardStats();
      setStats({ ...data, loading: false });
    } catch {
      setStats({ totalUsers: "Error", activeGroups: "Error", menuItems: "Error", loading: false });
    }
  };

  useEffect(() => { loadStats(); }, []);

  const handleLogout = async () => {
    await adminLogout();
    history.push("/admin/login");
  };
  const utilities = [
    // {
    //   type: "button",
    //   text: "Admin Portal",
    //   iconName: "settings",
    //   onClick: () => history.push("/admin"),
    // },
    {
      type: "menu-dropdown",
      text: adminUser ? `${adminUser.display_name || adminUser.email}` : "Account",
      iconName: "user-profile",
      onItemClick: ({ detail }) => {
        if (detail.id === "logout") {
          handleLogout();
        }
      },
      items: [
        { id: "user-info", text: adminUser?.display_name || adminUser?.email, disabled: true },
        { id: "divider", itemType: "divider" },
        { id: "logout", text: "Sign Out", iconName: "key" },
      ],
    },
  ];

  const tabs = [
    {
      id: "dashboard", label: "Dashboard",
      content: (
        <Container header={<Header actions={<Button onClick={loadStats} loading={stats.loading}>Refresh</Button>}>Admin Dashboard</Header>}>
          <SpaceBetween size="l">
            <Box>Welcome to the admin portal. Use the tabs to manage users, groups, menu permissions, and monitor security.</Box>
            <Cards
              cardDefinition={{
                header: i => i.title,
                sections: [
                  { id: "desc", content: i => i.description },
                  { id: "val", header: "Current Value", content: i => <Box fontSize="heading-xl" fontWeight="bold">{i.value}</Box> },
                ],
              }}
              cardsPerRow={[{ cards: 1 }, { minWidth: 500, cards: 2 }, { minWidth: 800, cards: 3 }]}
              loading={stats.loading}
              items={[
                { title: "Total Users", description: "Registered users in the system", value: stats.totalUsers },
                { title: "Active Groups", description: "User groups configured", value: stats.activeGroups },
                { title: "Menu Items", description: "Navigation items available", value: stats.menuItems },
              ]}
            />
          </SpaceBetween>
        </Container>
      ),
    },
    { id: "users", label: "User Management", content: <UserManagement /> },
    { id: "groups", label: "Group Management", content: <GroupManagement /> },
    { id: "menu", label: "Menu Management", content: <MenuManagement /> },
    { id: "admin-users", label: "Admin Users", content: <AdminUsers /> },
    { id: "security", label: "Security Monitoring", content: <SecurityMonitoring /> },
    { id: "analytics", label: "Usage Analytics", content: <UsageAnalytics /> },
  ];

  return (
    <div style={styles.page}>
      {/* <div style={styles.header}>
        <div style={styles.headerLeft}>
          <span style={styles.lock}>🔐</span>
          <span style={styles.title}>Admin Portal</span>
        </div>
        <div style={styles.headerRight}>
          <span style={styles.welcome}>Welcome, {adminUser?.display_name || adminUser?.email}</span>
          <Button size="small" onClick={handleLogout}>Logout</Button>
        </div>
      </div> */}
      <TopNavigation
        identity={{
          href: "/",
          title: `${adminUser?.display_name || adminUser?.email} SmartOps`,
          onFollow: (e) => { e.preventDefault(); history.push("/"); },
        }}
        utilities={utilities}
      />

      {notifications.length > 0 && (
        <div style={{ padding: "8px 24px 0" }}>
          <Flashbar items={notifications.map(n => ({
            ...n,
            onDismiss: () => removeNotification(n.id),
          }))} />
        </div>
      )}
      <div style={styles.content}>
        <ContentLayout>
          <SpaceBetween size="l">
            {accessAlert && (
              <Alert type="error">{accessAlert}</Alert>
            )}
            <Tabs
              activeTabId={activeTab}
              onChange={({ detail }) => setActiveTab(detail.activeTabId)}
              tabs={tabs}
            />
          </SpaceBetween>
        </ContentLayout>
      </div>
      <PoweredByFooter />
    </div>
  );
}

const styles = {
  page: { minHeight: "100vh", background: "#f0f2f5" },
  header: {
    background: "linear-gradient(135deg, #1a1f36 0%, #232f3e 100%)",
    padding: "16px 32px", display: "flex",
    justifyContent: "space-between", alignItems: "center",
  },
  headerLeft: { display: "flex", alignItems: "center", gap: 12 },
  lock: { fontSize: 24 },
  title: { color: "#fff", fontSize: 20, fontWeight: 700 },
  headerRight: { display: "flex", alignItems: "center", gap: 16 },
  welcome: { color: "#8fa3b1", fontSize: 14 },
  content: { padding: 24 },
};
